import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Calendar, MessageSquare, Star, Award, Languages, CheckCircle, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

interface Guide {
  id: number;
  name: string;
  languages: string[];
  specialties: string[];
  experience: number;
  image: string;
  rating: number;
  reviews: number;
}

const guides: Guide[] = [
  {
    id: 1,
    name: 'Agaba Mathius',
    image: 'https://imgur.com/ERlskFe.jpg',
    languages: ['English'],
    specialties: ['Wildlife Safaris'],
    experience: 7,
    location: "Uganda's National Parks",
    rating: 4.9,
    reviews: 156,
    description: 'Expert wildlife guide with extensive knowledge of Uganda\'s national parks and wildlife.'
  },
  {
    id: 2,
    name: 'Elisa Lule',
    image: 'https://imgur.com/lCTURxX.jpg',
    languages: ['English'],
    specialties: ['Kampala Citytours', 'Wildlife'],
    experience: 5,
    location: 'Kampala',
    rating: 4.8,
    reviews: 124,
    description: 'Skilled in both urban exploration and wildlife experiences, offering comprehensive tours.'
  },
  {
    id: 3,
    name: 'Ankunda Prima',
    image: 'https://imgur.com/ssIyPUG.jpg',
    languages: ['English'],
    specialties: ['City Tours', 'Culture Tours'],
    experience: 3,
    location: 'Kampala',
    rating: 4.7,
    reviews: 98,
    description: 'Passionate about sharing Uganda\'s rich cultural heritage and urban experiences.'
  },
  {
    id: 4,
    name: 'Niwagaba Esser',
    image: 'https://imgur.com/G5VWLDc.jpg',
    languages: ['English'],
    specialties: ['City Tours', 'Water Activities'],
    experience: 4,
    location: 'Jinja',
    rating: 4.8,
    reviews: 112,
    description: 'Specialized in Jinja city tours and exciting water activities on the Nile River.'
  }
];

const CustomizeAdventure = () => {
  const [selectedGuide, setSelectedGuide] = useState<number | null>(null);
  const [selectedExperiences, setSelectedExperiences] = useState<string[]>([]);
  const [selectedAdventures, setSelectedAdventures] = useState<string[]>([]);
  const [selectedCultural, setSelectedCultural] = useState<string[]>([]);
  const [selectedRelaxation, setSelectedRelaxation] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    days: '',
    notes: ''
  });
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const formBody = {
        'form-name': 'customize-adventure-form',
        ...formData,
        guide: guides.find(g => g.id === selectedGuide)?.name || 'No preference',
        experiences: selectedExperiences.join(', '),
        adventures: selectedAdventures.join(', '),
        cultural: selectedCultural.join(', '),
        relaxation: selectedRelaxation.join(', '),
        travelDate: startDate?.toLocaleDateString() || '',
      };

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formBody).toString()
      });

      if (response.ok) {
        window.location.href = '/success';
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('There was an error submitting your request. Please try again.');
    }
  };

  const toggleExperience = (category: 'experiences' | 'adventures' | 'cultural' | 'relaxation', item: string) => {
    const setters = {
      experiences: setSelectedExperiences,
      adventures: setSelectedAdventures,
      cultural: setSelectedCultural,
      relaxation: setSelectedRelaxation
    };

    const currentSelected = {
      experiences: selectedExperiences,
      adventures: selectedAdventures,
      cultural: selectedCultural,
      relaxation: selectedRelaxation
    };

    setters[category](prev => 
      prev.includes(item) 
        ? prev.filter(i => i !== item)
        : [...prev, item]
    );
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1516426122078-c23e76319801"
            alt="Customize your adventure"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold mb-4">{t('pages.customizeAdventure.title')}</h1>
            <p className="text-lg text-white/90">
              {t('pages.customizeAdventure.subtitle')}
            </p>
          </div>
        </div>
      </section>

      <form 
        name="customize-adventure-form"
        method="POST"
        data-netlify="true"
        onSubmit={handleSubmit}
        className="py-12 bg-white"
      >
        <input type="hidden" name="form-name" value="customize-adventure-form" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {/* Choose Your Guide */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('pages.customizeAdventure.chooseGuide')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {guides.map((guide) => (
                <div 
                  key={guide.id}
                  onClick={() => setSelectedGuide(guide.id)}
                  className={`cursor-pointer rounded-lg border ${
                    selectedGuide === guide.id 
                      ? 'border-ta-green ring-2 ring-ta-green/20' 
                      : 'border-gray-200'
                  } overflow-hidden transition-all duration-300 hover:shadow-lg`}
                >
                  <div className="relative h-32">
                    <img 
                      src={guide.image}
                      alt={guide.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center">
                      <Star className="h-3 w-3 text-yellow-400 fill-current" />
                      <span className="ml-1 text-xs font-medium">{guide.rating}</span>
                    </div>
                  </div>
                  
                  <div className="p-3">
                    <h3 className="text-sm font-medium text-gray-900">{guide.name}</h3>
                    
                    <div className="mt-1 space-y-1">
                      <div className="flex items-center text-xs text-gray-600">
                        <Languages className="h-3 w-3 text-ta-green mr-1" />
                        {guide.languages.join(', ')}
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <Award className="h-3 w-3 text-ta-green mr-1" />
                        {guide.experience} {t('common.years')} {t('common.experience')}
                      </div>
                    </div>
                    
                    <div className="mt-2 flex flex-wrap gap-1">
                      {guide.specialties.map((specialty, index) => (
                        <span 
                          key={index}
                          className="px-1.5 py-0.5 bg-ta-green/10 text-ta-green rounded-full text-[10px]"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Wildlife Experiences */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('pages.customizeAdventure.customizeExperience')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {[
                'Gorilla Trekking',
                'Chimpanzee Tracking',
                'Game Drive',
                'Birdwatching',
                'Fishing',
                'Boat Safaris',
                'Rhino Tracking'
              ].map((activity) => (
                <button
                  key={activity}
                  type="button"
                  onClick={() => toggleExperience('experiences', activity)}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    selectedExperiences.includes(activity)
                      ? 'border-ta-green bg-ta-green/5 text-ta-green'
                      : 'border-gray-200 hover:border-ta-green/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{activity}</span>
                    {selectedExperiences.includes(activity) && (
                      <CheckCircle className="h-4 w-4" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* Adventure Activities */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('common.adventureOutdoorActivities')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {[
                'White-Water Rafting',
                'Bungee Jumping',
                'Quad Biking & Horseback Riding',
                'Hiking & Mountain Climbing'
              ].map((activity) => (
                <button
                  key={activity}
                  type="button"
                  onClick={() => toggleExperience('adventures', activity)}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    selectedAdventures.includes(activity)
                      ? 'border-ta-green bg-ta-green/5 text-ta-green'
                      : 'border-gray-200 hover:border-ta-green/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{activity}</span>
                    {selectedAdventures.includes(activity) && (
                      <CheckCircle className="h-4 w-4" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* Cultural Experiences */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('common.culturalCityExperiences')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {[
                'Kampala City Tour',
                'Local Market Tour',
                'Cultural Performances',
                'Visit the Equator'
              ].map((activity) => (
                <button
                  key={activity}
                  type="button"
                  onClick={() => toggleExperience('cultural', activity)}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    selectedCultural.includes(activity)
                      ? 'border-ta-green bg-ta-green/5 text-ta-green'
                      : 'border-gray-200 hover:border-ta-green/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{activity}</span>
                    {selectedCultural.includes(activity) && (
                      <CheckCircle className="h-4 w-4" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* Relaxation Experiences */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('common.relaxationNature')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {[
                'Sipi Falls Tour',
                'Lake Bunyonyi Retreat',
                'Hot Springs Visit',
                'Ssese Islands Getaway'
              ].map((activity) => (
                <button
                  key={activity}
                  type="button"
                  onClick={() => toggleExperience('relaxation', activity)}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    selectedRelaxation.includes(activity)
                      ? 'border-ta-green bg-ta-green/5 text-ta-green'
                      : 'border-gray-200 hover:border-ta-green/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{activity}</span>
                    {selectedRelaxation.includes(activity) && (
                      <CheckCircle className="h-4 w-4" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* Final Details */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('pages.customizeAdventure.finalDetails')}</h2>
            <div className="max-w-2xl">
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('common.name')} *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('common.email')} *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('common.numberOfDays')} *</label>
                    <select
                      name="days"
                      required
                      value={formData.days}
                      onChange={(e) => setFormData({ ...formData, days: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                    >
                      <option value="">{t('common.selectDuration')}</option>
                      {[1,2,3,4,5,6,7,8,9,10,14,21].map(num => (
                        <option key={num} value={num}>{num} {num === 1 ? t('common.day') : t('common.days')}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('common.preferredTravelDate')}</label>
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      minDate={new Date()}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                      placeholderText={t('common.selectDate')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('common.additionalNotes')}</label>
                  <textarea
                    name="notes"
                    rows={4}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                    placeholder={t('common.anySpecialRequests')}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-ta-green hover:bg-ta-green-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ta-green"
                >
                  {t('pages.customizeAdventure.requestQuote')}
                  <ChevronRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>
          </section>
        </div>
      </form>

      <Footer />
    </>
  );
};

export default CustomizeAdventure;